import { Box, Skeleton, styled } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import React, { useCallback } from 'react';
import { EColors, getColor } from 'theme';

const TOGGLE_BUTTON_SKELETON_CONFIG = {
    buttonCount: 4,
    // Approximate width for typical tab labels like "Overview", "Insights", "Location"
    buttonWidth: 100,
    buttonHeight: '100%',
    gapBetweenButtons: 0,
    // Match actual button padding: '0.7345rem 2.219rem'
    padding: '0.7345rem 2.219rem',
} as const;

const SkeletonContainer = styled(Box)({
    // Match actual container padding: '4px'
    padding: '4px',
    background: getColor(EColors.v3LightGrayBackground),
    borderRadius: '0.5rem',
    display: 'inline-flex',
});

const SkeletonButtonWrapper = styled(Box)({
    padding: TOGGLE_BUTTON_SKELETON_CONFIG.padding,
    position: 'relative',
    '&:not(:first-of-type)': {
        marginLeft: -1,
    },
});

const SkeletonButton = styled(Skeleton, {
    shouldForwardProp: prop => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ isSelected }) => ({
    borderRadius: '0.25rem',
    backgroundColor: isSelected ? getColor(EColors.pureWhite) : getColor(EColors.pureWhite, 0.3),
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => {
    const { shape } = theme;
    return {
        [`&.${toggleButtonGroupClasses.root}`]: {
            borderRadius: Number(shape.borderRadius) * 2,
            [theme.breakpoints.down('sm')]: {
                flexWrap: 'nowrap',
            },
        },
        [`& .${toggleButtonGroupClasses.grouped}`]: {
            border: 0,
            borderRadius: shape.borderRadius,
            borderLeft: '1px solid transparent',
            padding: '0.7345rem 2.219rem',
            lineHeight: 'normal',
            [theme.breakpoints.down('sm')]: {
                padding: '0.5rem 0.75rem',
                fontSize: '0.8125rem',
            },
            [`&.${toggleButtonGroupClasses.disabled}`]: {
                border: 0,
            },
            color: getColor(EColors.pureBlack, 0.5),
        },
        [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
            marginLeft: -1,
        },
        [`& .${toggleButtonGroupClasses.selected}`]: {
            background: getColor(EColors.pureWhite),
            color: getColor(EColors.pureBlack),
        },
        [`& .${toggleButtonGroupClasses.disabled}`]: {
            background: getColor(EColors.v3LightGrayBackground),
            color: getColor(EColors.pureBlack, 0.1),
        },
    };
});

export type ButtonOption = {
    value: string;
    label: string;
    onClick?: () => void;
};

type ToggleButtonsProps = {
    options: ButtonOption[];
    value?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
};

export function ToggleButtons({ options, value = options[0]?.value, onChange, disabled }: ToggleButtonsProps) {
    const handleValueChange = useCallback(
        (_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
            if (newValue && onChange) {
                onChange(newValue);
            }
        },
        [onChange]
    );

    if (options.length === 0) {
        return (
            <SkeletonContainer>
                {Array.from({ length: TOGGLE_BUTTON_SKELETON_CONFIG.buttonCount }).map((_, index) => (
                    <SkeletonButtonWrapper key={`toggle-button-skeleton-${index}`}>
                        <SkeletonButton
                            variant="rectangular"
                            animation="pulse"
                            width={TOGGLE_BUTTON_SKELETON_CONFIG.buttonWidth}
                            height={TOGGLE_BUTTON_SKELETON_CONFIG.buttonHeight}
                            isSelected={true}
                        />
                    </SkeletonButtonWrapper>
                ))}
            </SkeletonContainer>
        );
    }

    return (
        <StyledToggleButtonGroup
            disabled={disabled}
            value={value}
            exclusive
            onChange={handleValueChange}
            aria-label="text alignment"
            sx={{ padding: '4px', background: getColor(EColors.v3LightGrayBackground), borderRadius: '0.5rem' }}
        >
            {options.map(option => (
                <ToggleButton
                    key={option.value}
                    value={option.value}
                    aria-label={`${option.value} aligned`}
                    onClick={() => {
                        if (onChange) {
                            onChange(option.value);
                        }
                        option.onClick?.();
                    }}
                >
                    {option.label}
                </ToggleButton>
            ))}
        </StyledToggleButtonGroup>
    );
}
